import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { activeJourneyId, JOURNEYS } from "./nav";

export type NavigationLayout = "compact" | "medium" | "expanded";

interface PrimaryNavigationProps {
  layout: NavigationLayout;
  appBarHeight: string;
}

export function PrimaryNavigation({
  layout,
  appBarHeight,
}: PrimaryNavigationProps) {
  const { pathname } = useLocation();
  const activeId = activeJourneyId(pathname);

  if (layout === "compact") {
    return (
      <Paper
        component="nav"
        aria-label="Primary bottom navigation"
        square
        sx={{
          position: "fixed",
          insetInline: 0,
          bottom: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          borderTop: "1px solid",
          borderColor: "divider",
          pb: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <BottomNavigation
          showLabels
          value={activeId}
          sx={{ height: "auto", minHeight: "4rem" }}
        >
          {JOURNEYS.map((journey) => {
            const JourneyIcon = journey.icon;
            return (
              <BottomNavigationAction
                key={journey.id}
                component={RouterLink}
                to={journey.to}
                aria-current={activeId === journey.id ? "page" : undefined}
                value={journey.id}
                label={journey.label}
                icon={<JourneyIcon fontSize="small" aria-hidden="true" />}
                aria-label={journey.label}
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                  maxWidth: "none",
                  minHeight: "4rem",
                  px: 0.5,
                  py: 0.75,
                  gap: 0.5,
                  "& .MuiBottomNavigationAction-label": {
                    width: "100%",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    textAlign: "center",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    lineHeight: 1.15,
                    "&.Mui-selected": { fontSize: "0.75rem" },
                  },
                }}
              />
            );
          })}
        </BottomNavigation>
      </Paper>
    );
  }

  const expanded = layout === "expanded";
  const drawerWidth = expanded ? 224 : 80;

  return (
    <Drawer
      variant="permanent"
      open
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          top: appBarHeight,
          width: drawerWidth,
          height: `calc(100dvh - ${appBarHeight})`,
          boxSizing: "border-box",
          px: expanded ? 1.5 : 1,
          py: 2.5,
        },
      }}
    >
      <Box
        component="nav"
        aria-label={expanded ? "Primary navigation" : "Primary navigation rail"}
      >
        <List disablePadding>
          {JOURNEYS.map((journey) => {
            const JourneyIcon = journey.icon;
            const selected = activeId === journey.id;
            return (
              <ListItemButton
                key={journey.id}
                component={RouterLink}
                to={journey.to}
                selected={selected}
                aria-current={selected ? "page" : undefined}
                aria-label={journey.label}
                sx={{
                  minHeight: 48,
                  mb: 0.75,
                  borderRadius: "10px",
                  justifyContent: expanded ? "initial" : "center",
                  px: expanded ? 1.5 : 1,
                  "&::before": selected
                    ? {
                        content: '""',
                        position: "absolute",
                        insetInlineStart: 0,
                        top: 14,
                        bottom: 14,
                        width: 3,
                        borderRadius: "2px",
                        bgcolor: "primary.main",
                      }
                    : undefined,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: expanded ? 38 : 0,
                    justifyContent: "center",
                    color: selected ? "semantic.primary" : "text.secondary",
                  }}
                >
                  <JourneyIcon fontSize="small" aria-hidden="true" />
                </ListItemIcon>
                {expanded ? (
                  <ListItemText
                    primary={journey.label}
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: "0.9rem",
                          fontWeight: selected ? 650 : 500,
                        },
                      },
                    }}
                  />
                ) : null}
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
}
